import React, { useContext, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CAlert, CButton, CCard, CCardBody, CForm, CFormTextarea, CSpinner,
} from '@coreui/react-pro'

import { api, ApiError } from '../api/client'
import { AuthContext } from '../App'
import { labelForEmail, usePeople } from '../api/people'
import { canEdit } from '../auth/capabilities'
import Linkify from '../components/Linkify'

interface Idea {
  id: number
  idea: string
  notes: string
  links: string
  comment_count: number
  discussion_summary: string
}

interface Comment {
  id: number
  idea_id: number
  author: string
  body: string
  created_at: string
}

/** Backend stores naive UTC; tag it so Date parses it as such. */
function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/** Message board for one idea — the URL is stable, so it can be shared in email. */
const IdeaDiscussion: React.FC = () => {
  const { id } = useParams()
  const { auth } = useContext(AuthContext)
  const queryClient = useQueryClient()
  const people = usePeople()
  const who = (email: string | null | undefined) =>
    labelForEmail(people.data, email) || email || ''
  const [body, setBody] = useState('')
  const [error, setError] = useState<string | null>(null)

  const idea = useQuery({
    queryKey: ['idea', id],
    queryFn: () => api.get<Idea>(`/v1/ideas/${id}`),
  })
  const comments = useQuery({
    queryKey: ['idea-comments', id],
    queryFn: () => api.get<Comment[]>(`/v1/ideas/${id}/comments`),
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['idea-comments', id] })
    queryClient.invalidateQueries({ queryKey: ['idea', id] })
    queryClient.invalidateQueries({ queryKey: ['ideas'] })
  }

  const post = useMutation({
    mutationFn: () => api.post(`/v1/ideas/${id}/comments`, { body }),
    onSuccess: () => {
      setBody('')
      setError(null)
      invalidate()
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to post — try again.')
    },
  })

  const remove = useMutation({
    mutationFn: (cid: number) => api.delete(`/v1/ideas/${id}/comments/${cid}`),
    onSuccess: invalidate,
  })

  if (idea.isLoading) {
    return <div className="text-center py-5"><CSpinner /></div>
  }
  if (idea.isError || !idea.data) {
    return <CAlert color="danger">Couldn't load this idea — it may have been deleted.</CAlert>
  }
  const data = idea.data

  return (
    <>
      <div className="mb-3">
        <Link to="/ideas" className="text-decoration-none">← 💡 All ideas</Link>
      </div>
      <CCard className="mb-4">
        <CCardBody>
          <h4 className="mb-1">💡 {data.idea}</h4>
          {data.links && (
            <div>
              {data.links.split('\n').filter(Boolean).map((u, i) => (
                <a key={i} href={u} target="_blank" rel="noopener noreferrer">{u}</a>
              ))}
            </div>
          )}
          {data.notes && (
            <div className="text-body-secondary" style={{ whiteSpace: 'pre-wrap' }}>
              <Linkify text={data.notes} />
            </div>
          )}
          {data.discussion_summary && (
            <CAlert color="info" className="mt-2 mb-0 py-2">
              🤖 {data.discussion_summary}
            </CAlert>
          )}
        </CCardBody>
      </CCard>

      <h5 className="mb-3">💬 Discussion ({comments.data?.length ?? 0})</h5>
      {comments.isLoading ? (
        <div className="text-center py-3"><CSpinner /></div>
      ) : (comments.data ?? []).length === 0 ? (
        <p className="text-body-secondary">No comments yet — start the discussion.</p>
      ) : (
        (comments.data ?? []).map((c) => (
          <CCard key={c.id} className="mb-2">
            <CCardBody className="py-2">
              <div className="d-flex align-items-center mb-1">
                <div className="small text-body-secondary">
                  {who(c.author)} · {fmtWhen(c.created_at)}
                </div>
                {auth.user?.role === 'admin' && (
                  <CButton
                    size="sm" color="danger" variant="outline" className="ms-auto py-0"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm('Delete this comment?')) remove.mutate(c.id)
                    }}
                  >
                    Delete
                  </CButton>
                )}
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{c.body}</div>
            </CCardBody>
          </CCard>
        ))
      )}

      {canEdit(auth.user?.role) ? (
        <CForm
          className="mt-3"
          onSubmit={(e) => {
            e.preventDefault()
            if (body.trim()) post.mutate()
          }}
        >
          <CFormTextarea
            rows={3}
            placeholder="Add to the discussion…"
            value={body}
            maxLength={5000}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="d-flex align-items-center gap-2 mt-2">
            <CButton color="primary" type="submit" disabled={post.isPending || !body.trim()}>
              {post.isPending ? <CSpinner size="sm" /> : 'Post comment'}
            </CButton>
            {error && <span className="small text-danger">{error}</span>}
          </div>
        </CForm>
      ) : (
        <p className="small text-body-secondary mt-3">
          Viewers can read the discussion; signing in with edit rights lets you post.
        </p>
      )}
    </>
  )
}

export default IdeaDiscussion
